import { useMemo, useState } from 'react'
import {
  Alert, Anchor, Badge, Button, Group, Modal, Stack, Table, Text, TextInput, Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconAlertTriangle, IconPlus, IconRobot } from '@tabler/icons-react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import { DataCard, EmptyState } from '@/components/ui'
import { useCanNaPlataforma } from '@/store/auth'
import {
  CHAVE_FROTA, MODELO_LABEL, cadastrarRobo, placaRepetida, useFrota,
  type CredencialEmitida, type HardwareModel, type RoboFrota,
} from '@/api/robo'
import { CredencialModal } from './CredencialModal'
import { FirmwareSelo } from './FirmwareSelo'
import { LerRoboConectado, type PlacaLida } from './LerRoboConectado'
import { RedePadrao } from './RedePadrao'
import { SeletorDeLocal } from './SeletorDeLocal'
import { nomeOuFallback } from './nomeDoRobo'
import { SeletorDeModelo } from './SeletorDeModelo'
import { selectableUsers, type MixedUserRow } from '@/utils/realUsers'
import { avisoDeCredencial, estadoDoRobo, seloDeEntrega, type ChaveAviso } from './estado'

type UsuarioLista = MixedUserRow & { full_name?: string; email?: string }

function Credencial({ robo, agora }: {
  robo: { aviso_credencial: ChaveAviso; token_expires_at: number | null }; agora: number
}) {
  const aviso = avisoDeCredencial(robo, agora)
  if (aviso.chave === 'sem_credencial') return <Text size="sm" c="dimmed">Não emitida</Text>
  if (aviso.chave === 'vencida') return <Badge color="red" variant="light">Vencida</Badge>
  if (aviso.chave === 'vence_em_breve') {
    // Template literal, e nao `Vence em {n} dias` em JSX: o JSX quebraria isto
    // em tres nos de texto, e a frase deixaria de existir como uma frase.
    return (
      <Badge color="yellow" variant="light">
        {`Vence em ${aviso.diasRestantes} ${aviso.diasRestantes === 1 ? 'dia' : 'dias'}`}
      </Badge>
    )
  }
  return (
    <Text size="xs" ff="monospace">
      {new Date((robo.token_expires_at as number) * 1000).toLocaleDateString('pt-BR')}
    </Text>
  )
}

type Rascunho = {
  name: string
  location: string
  modelo: HardwareModel | null
  /** O que a placa no cabo respondeu. Com ela, o modelo e o MAC sao fato lido. */
  placa: PlacaLida | null
}

const VAZIO: Rascunho = { name: '', location: '', modelo: null, placa: null }

/** A recusa do cadastro, dita em frase. `placa` leva ao robo que ja' a tem. */
type Recusa = { tipo: 'placa'; publicId: string | null } | { tipo: 'outro' }

function SeloDeEntrega({ robo, agora }: { robo: RoboFrota; agora: number }) {
  const selo = seloDeEntrega(robo, agora)
  return selo ? <Badge color={selo.cor} variant="light" size="sm">{selo.rotulo}</Badge> : null
}

export function Frota() {
  // A frota e' da plataforma (spec 2026-09-24-robo-sem-cliente, D1): o robo
  // nasce sem cliente, e cadastrar pede `robo:manage` GLOBAL — a mesma
  // pergunta que o backend faz. O seletor do topo nao entra.
  const can = useCanNaPlataforma()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [cadastrando, ctlCadastro] = useDisclosure(false)
  const [rascunho, setRascunho] = useState<Rascunho>(VAZIO)
  const [recusa, setRecusa] = useState<Recusa | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [emitida, setEmitida] = useState<CredencialEmitida | null>(null)
  const { data: robos, isLoading, isError } = useFrota()

  // Uma consulta serve duas coisas: o nome do dono nesta lista e o seletor da
  // atribuicao. A rota da frota devolve `owner_user_id`, nunca o nome.
  const { data: usuarios } = useQuery({
    queryKey: ['usuarios-para-robo'],
    // `/v1/users` devolve `{items: [...]}` (`users_list` em api.py termina em
    // `return {'items': items}`), NUNCA um array. Tipar o `get` e' o que
    // impede a anotacao de mentir: sem o parametro de tipo, `.data` e' `any`
    // e o `tsc` aceita `Promise<UsuarioLista[]>` sobre um objeto — foi assim
    // que a tela caiu em producao com `e.filter is not a function`.
    queryFn: async (): Promise<UsuarioLista[]> =>
      (await api.get<{ items: UsuarioLista[] }>('/v1/users')).data.items ?? [],
    staleTime: 5 * 60 * 1000,
  })
  const nomePorId = useMemo(() => {
    const m = new Map<number, string>()
    selectableUsers(usuarios ?? []).forEach((u) =>
      m.set(u.id, u.full_name || u.email || `usuário #${u.id}`))
    return m
  }, [usuarios])

  const agora = Date.now()

  const corpo = () => {
    if (isLoading) return <Text size="sm" c="dimmed">Carregando a frota…</Text>
    if (isError) {
      return (
        <EmptyState
          icon={IconRobot}
          accent="danger"
          size="md"
          title="Não foi possível ler a frota"
          description="A consulta ao registro de robôs falhou. Se persistir, é caso de olhar o log da API."
        />
      )
    }
    if (!robos?.length) {
      return (
        <EmptyState
          icon={IconRobot}
          size="md"
          title="Nenhum robô que você possa ver"
          description="Um robô é cadastrado aqui e depois atribuído a um funcionário — é a atribuição que lhe dá um dono, e é o dono quem lhe dá um nome."
        />
      )
    }
    return (
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Robô</Table.Th>
            <Table.Th>Estado</Table.Th>
            <Table.Th>Última comunicação</Table.Th>
            <Table.Th>Dono</Table.Th>
            <Table.Th>Credencial</Table.Th>
            <Table.Th>Modelo</Table.Th>
            <Table.Th>Firmware</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {robos.map((r: RoboFrota) => {
            const e = estadoDoRobo(r)
            return (
              <Table.Tr
                key={r.public_id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/robo/frota/${encodeURIComponent(r.public_id)}`)}
              >
                <Table.Td>
                  <Text size="sm" fw={600}>{nomeOuFallback(r)}</Text>
                  {r.location && <Text size="xs" c="dimmed">{r.location}</Text>}
                </Table.Td>
                <Table.Td>
                  <Tooltip label={e.significado} multiline w={260}>
                    <Badge color={e.cor} variant="light">{e.rotulo}</Badge>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  {r.last_seen_at
                    ? (
                      <Text size="xs" ff="monospace">
                        {new Date(r.last_seen_at).toLocaleString('pt-BR')}
                      </Text>
                      )
                    : <Text size="sm" c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td>
                  {r.owner_user_id
                    ? (
                      <Stack gap={2} align="flex-start">
                        <Text size="sm">
                          {nomePorId.get(r.owner_user_id) ?? `usuário #${r.owner_user_id}`}
                        </Text>
                        <SeloDeEntrega robo={r} agora={agora} />
                      </Stack>
                      )
                    : <Text size="sm" c="dimmed">Sem dono</Text>}
                </Table.Td>
                <Table.Td><Credencial robo={r} agora={agora} /></Table.Td>
                <Table.Td>
                  {r.hardware_model
                    ? <Text size="sm">{MODELO_LABEL[r.hardware_model]}</Text>
                    : <Text size="sm" c="dimmed">Não informado</Text>}
                </Table.Td>
                <Table.Td><FirmwareSelo firmware={r.firmware} bruto={r.firmware_version} /></Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    )
  }

  return (
    <Stack gap="md">
      {/* Sem PageHeader proprio: o titulo e a trilha vem do AppLayout, via
          `routeHeader`/`routeRegistry`. Pagina que desenha o seu produz duas
          trilhas na mesma tela, e ha guarda para isso
          (`AppLayout.cabecalho.test.tsx`). */}
      {/* A rede Wi-Fi padrão (spec 2026-09-29-robo-wifi-padrao, 4.2): acima da
          lista, e só para quem tem `robo:manage` GLOBAL, como a rota. */}
      {can('robo', 'manage') && (
        <RedePadrao nomeDe={(id) => (id == null ? null : nomePorId.get(id) ?? null)} />
      )}
      <DataCard title="Robôs">
        <Stack gap="sm">
          {/* A ressalva e o botao na MESMA linha: o botao sozinho numa faixa
              propria empurrava a explicacao para baixo dele, e a explicacao e'
              o que a pessoa precisa ler antes de acreditar na coluna Estado. */}
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="lg">
            <Text size="xs" c="dimmed" style={{ maxWidth: 860 }}>
              O estado vem da última vez que o robô falou com o Console. Ele não
              verifica se o display está aceso nem se a imagem exibida está correta.
            </Text>
            {can('robo', 'manage') && (
              <Button
                style={{ flexShrink: 0 }}
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setRascunho(VAZIO)
                  setRecusa(null)
                  ctlCadastro.open()
                }}
              >
                Cadastrar robô
              </Button>
            )}
          </Group>
          {corpo()}
        </Stack>
      </DataCard>

      <Modal opened={cadastrando} onClose={ctlCadastro.close} title="Cadastrar robô">
        <Stack gap="sm">
          {/* A placa primeiro: modelo e MAC sao fato de hardware, e quem
              cadastra so' os escolhe a mao quando a placa nao pode dizer. */}
          <LerRoboConectado
            onLido={(placa) => {
              setRecusa(null)
              // A ESP32 nao tem numero de serie de fabrica (`esptool chip-id`
              // devolve o MAC no lugar), entao o MAC e' o identificador que
              // sugere o nome. So' ocupa campo vazio ou a sugestao anterior:
              // o que a pessoa digitou nao se apaga.
              setRascunho((r) => ({
                ...r,
                modelo: placa.perfil,
                placa,
                name: !r.name.trim() || r.name === r.placa?.mac ? placa.mac : r.name,
              }))
            }}
          />
          {rascunho.placa && (
            <Text size="xs" ff="monospace">
              {`Placa ${rascunho.placa.mac} · firmware ${rascunho.placa.versao}`}
            </Text>
          )}
          <TextInput
            label="Número de série do inventário"
            description="Identifica o robô na frota. Lida a placa, vem o MAC dela. O dono pode dar ao robô o nome que quiser."
            value={rascunho.name}
            onChange={(ev) => setRascunho((r) => ({ ...r, name: ev.currentTarget.value }))}
          />
          <SeletorDeLocal
            value={rascunho.location}
            onChange={(location) => setRascunho((r) => ({ ...r, location }))}
          />
          <SeletorDeModelo
            value={rascunho.modelo}
            disabled={!!rascunho.placa}
            description={rascunho.placa
              ? 'Lido da placa conectada. Define o firmware que o Console grava.'
              : undefined}
            onChange={(modelo) => setRascunho((r) => ({ ...r, modelo }))}
          />
          {recusa?.tipo === 'placa' && (
            <Alert color="yellow" icon={<IconAlertTriangle size={18} />}>
              {'Esta placa já é o robô '}
              {recusa.publicId
                ? (
                  <Anchor component={Link} to={`/robo/frota/${encodeURIComponent(recusa.publicId)}`}>
                    {robos?.find((r) => r.public_id === recusa.publicId)?.name ?? recusa.publicId}
                  </Anchor>
                  )
                : 'que já está na frota'}
              . Uma placa viva tem um robô só; para recadastrá-la, aposente o robô atual.
            </Alert>
          )}
          {recusa?.tipo === 'outro' && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              Não foi possível cadastrar o robô. Tente de novo; se persistir, é caso de olhar o log da API.
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={ctlCadastro.close}>Cancelar</Button>
            <Button
              disabled={!rascunho.name.trim()}
              loading={enviando}
              onClick={async () => {
                setRecusa(null)
                setEnviando(true)
                try {
                  const novo = await cadastrarRobo({
                    name: rascunho.name.trim(),
                    location: rascunho.location.trim() || null,
                    // Sem escolha, o campo nao vai: "ainda nao sei" e' diferente de chutar.
                    ...(rascunho.modelo ? { hardware_model: rascunho.modelo } : {}),
                    ...(rascunho.placa ? { mac: rascunho.placa.mac } : {}),
                  })
                  ctlCadastro.close()
                  qc.invalidateQueries({ queryKey: CHAVE_FROTA })
                  // A credencial vem no 201 e nao volta: mostrar e' parte do cadastro.
                  setEmitida(novo)
                } catch (erro) {
                  const repetida = placaRepetida(erro)
                  setRecusa(repetida ? { tipo: 'placa', ...repetida } : { tipo: 'outro' })
                } finally {
                  setEnviando(false)
                }
              }}
            >
              Cadastrar
            </Button>
          </Group>
        </Stack>
      </Modal>

      <CredencialModal
        credencial={emitida}
        onClose={() => {
          const novo = emitida?.public_id
          setEmitida(null)
          // Depois de copiar, o proximo passo e' no robo novo: dono, USB, firmware.
          if (novo) navigate(`/robo/frota/${encodeURIComponent(novo)}`)
        }}
      />
    </Stack>
  )
}
